import React, { useState, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  Label
} from "reactstrap";
import { getDailyBookingSummary, exportDailyBookingSummaryToExcel } from "../../components/Api/GmsBooking";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import Select from "react-select";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// ─── Session helper ───────────────────────────────────────────────────────────
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const normalizeId = (v) => {
    if (v === null || v === undefined || v === "" || v === "null") return null;
    const n = typeof v === "string" ? parseInt(v, 10) : v;
    return isNaN(n) || n <= 0 ? null : n;
  };
  return {
    businessId: normalizeId(authUser.companyId || user.user_Company_Id),
    branchId: normalizeId(authUser.branchId || user.user_Branch_Id),
  };
};

const selectStyles = {
  control: (p, s) => ({
    ...p,
    minHeight: "38px",
    borderColor: s.isFocused ? "#556ee6" : "#ced4da",
    boxShadow: s.isFocused ? "0 0 0 0.2rem rgba(85,110,230,0.25)" : "none",
  }),
  menu: (p) => ({ ...p, zIndex: 9999, maxHeight: 200 }),
  menuList: (p) => ({ ...p, maxHeight: 180, overflowY: "auto" }),
};

const DailyBookingSummaryPage = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState([]);
  
  // Default range: Start of month to today
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  
  const [fromDate, setFromDate] = useState(startOfMonth);
  const [toDate, setToDate] = useState(today);

  const session = useMemo(() => getUserSession(), []);
  
  // Dropdown options
  const [businessOptions, setBusinessOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  
  // Selected values
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // ── Load businesses on mount, auto-bind from session ─────────────────────
  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await getBusinesses({ business_Active: true });
        const data = Array.isArray(res)
          ? res
          : res?.Businesses || res?.businesses || res?.data || [];
        const opts = data.map((b) => ({
          value: b.Business_Id || b.business_Id,
          label: b.Business_NameEn || b.business_NameEn,
        }));
        setBusinessOptions(opts);
        if (session.businessId) {
          const match = opts.find((o) => o.value === Number(session.businessId));
          if (match) setSelectedBusiness(match);
        }
      } catch { /* silent */ }
    };
    load();
  }, [session.businessId]);

  // ── Load branches whenever selected business changes ──────────────────────
  React.useEffect(() => {
    const load = async () => {
      setBranchOptions([]);
      setSelectedBranch(null);
      if (!selectedBusiness) return;
      try {
        const res = await getBranches({
          Branch_Buisness_Id: selectedBusiness.value,
          branch_Active: true,
        });
        const data = Array.isArray(res)
          ? res
          : res?.Branches || res?.branches || res?.data || [];
        const opts = data.map((b) => ({
          value: b.Branch_Id || b.branch_Id,
          label: b.Branch_NameEn || b.branch_NameEn,
        }));
        setBranchOptions(opts);
        if (session.branchId) {
          const match = opts.find((o) => o.value === Number(session.branchId));
          if (match) setSelectedBranch(match);
        }
      } catch { /* silent */ }
    };
    load();
  }, [selectedBusiness, session.branchId]);

  const fetchReport = async () => {
    if (!selectedBusiness || !selectedBranch) {
      Swal.fire("Validation", "Please select both Company and Branch before searching.", "warning");
      return;
    }
    if (!fromDate || !toDate) {
      toast.warning("Please select both From and To dates.");
      return;
    }

    if (fromDate > toDate) {
      toast.warning("From Date cannot be later than To Date.");
      return;
    }

    setLoading(true);
    try {
      const response = await getDailyBookingSummary({
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      });

      if (response && Array.isArray(response)) {
        setReportData(response);
      } else {
        toast.error("Failed to fetch report data.");
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("An error occurred while fetching the report.");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!selectedBusiness || !selectedBranch) {
      Swal.fire("Validation", "Please select both Company and Branch before exporting.", "warning");
      return;
    }
    if (!fromDate || !toDate) {
      toast.warning("Please select both From and To dates.");
      return;
    }

    setExporting(true);
    try {
      await exportDailyBookingSummaryToExcel({
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        businessId: selectedBusiness.value,
        branchId: selectedBranch.value
      });
      toast.success("Export successful");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("An error occurred during export");
    } finally {
      setExporting(false);
    }
  };

  // Group data by Month-Year for subtotals
  const processedData = useMemo(() => {
    if (!reportData || reportData.length === 0) return { grouped: {}, grandTotal: { bookings: 0, clients: 0 } };

    const grouped = {};
    let grandTotalBookings = 0;
    let grandTotalClients = 0;

    reportData.forEach(item => {
      const monthKey = `${item.monthName} ${item.year}`;
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          items: [],
          totalBookings: 0,
          totalClients: 0
        };
      }
      
      grouped[monthKey].items.push(item);
      grouped[monthKey].totalBookings += item.totalBookings || 0;
      grouped[monthKey].totalClients += item.uniqueClients || 0;
      
      grandTotalBookings += item.totalBookings || 0;
      grandTotalClients += item.uniqueClients || 0;
    });

    return { 
      grouped, 
      grandTotal: { bookings: grandTotalBookings, clients: grandTotalClients } 
    };
  }, [reportData]);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Reports" breadcrumbItem="Daily Booking Summary" />

          <Card>
            <CardBody>
              <Row className="mb-4 align-items-end g-2">
                <Col md={3}>
                  <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>
                    Company <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={businessOptions}
                    value={selectedBusiness}
                    onChange={(opt) => {
                      setSelectedBusiness(opt);
                      setSelectedBranch(null);
                    }}
                    styles={selectStyles}
                    placeholder="Select Company"
                    isClearable={false}
                  />
                </Col>
                <Col md={3}>
                  <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>
                    Branch <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={branchOptions}
                    value={selectedBranch}
                    onChange={setSelectedBranch}
                    styles={selectStyles}
                    placeholder={selectedBusiness ? "Select Branch" : "Select Company first"}
                    isClearable={false}
                    isDisabled={!selectedBusiness}
                  />
                </Col>
                <Col md={2}>
                  <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>From Date</Label>
                  <DatePicker
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select From Date"
                  />
                </Col>
                <Col md={2}>
                  <Label className="fw-semibold mb-1" style={{ fontSize: "0.82rem" }}>To Date</Label>
                  <DatePicker
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select To Date"
                    minDate={fromDate}
                  />
                </Col>
                <Col md={2}>
                  <div className="d-flex gap-2 w-100">
                    <Button color="primary" onClick={fetchReport} disabled={loading} className="text-nowrap flex-grow-1">
                      {loading ? <Spinner size="sm" /> : "Search"}
                    </Button>
                    
                    {reportData.length > 0 && (
                      <Button 
                        color="success"
                        onClick={handleExportExcel}
                        disabled={exporting}
                        className="text-nowrap"
                      >
                        {exporting ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-export me-1"></i>}
                        Export Excel
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>

              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table className="table table-bordered table-hover mb-0 text-nowrap align-middle" style={{ fontSize: "0.82rem" }}>
                  <thead style={{ backgroundColor: "#f0f3ff" }}>
                    <tr>
                      <th>Date</th>
                      <th>Day of Week</th>
                      <th className="text-end">Total Bookings</th>
                      <th className="text-end">Unique Clients</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <Spinner color="primary" />
                          <div className="mt-2">Fetching data...</div>
                        </td>
                      </tr>
                    ) : reportData.length > 0 ? (
                      Object.keys(processedData.grouped).map(monthKey => (
                        <React.Fragment key={monthKey}>
                          {/* Data rows for this month */}
                          {processedData.grouped[monthKey].items.map((row, index) => {
                            const d = new Date(row.bookingDate);
                            const formattedDate = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                            return (
                              <tr key={`${monthKey}-${index}`}>
                                <td>{formattedDate}</td>
                                <td>{row.dayOfWeek}</td>
                                <td className="text-end">{row.totalBookings}</td>
                                <td className="text-end">{row.uniqueClients}</td>
                              </tr>
                            );
                          })}
                          
                          {/* Monthly Subtotal Row */}
                          <tr style={{ backgroundColor: "#fdfdfd", fontWeight: "bold" }}>
                            <td colSpan="2" className="text-end" style={{ color: "#556ee6" }}>
                              {monthKey} Total:
                            </td>
                            <td className="text-end text-primary">{processedData.grouped[monthKey].totalBookings}</td>
                            <td className="text-end text-primary">{processedData.grouped[monthKey].totalClients}</td>
                          </tr>
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          No data found for the selected period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {reportData.length > 0 && (
                    <tfoot style={{ backgroundColor: "#f0f3ff", fontWeight: 'bold' }}>
                      <tr>
                        <td colSpan="2" className="text-end text-danger">Grand Total:</td>
                        <td className="text-end text-danger">{processedData.grandTotal.bookings}</td>
                        <td className="text-end text-danger">{processedData.grandTotal.clients}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              
            </CardBody>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default DailyBookingSummaryPage;
